import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import ReactSlider from 'react-slider';
import type { ClassNameProp } from '../common-types';
import { HBox, VBox } from '../layout';
import { NumberInput } from '../form';
import _ from 'lodash';
import { intl, intlFmtTemplateString } from '../../lib';

/**
 * Props for a {@link Slider} component.
 */
export interface SliderProps extends ClassNameProp {
  /**
   * Aria-label for screen-readers to apply to the thumb.
   */
  ariaLabel?: string;
  /**
   * Aria-labelledby for screen-readers to apply to the thumb.
   */
  ariaLabelledby?: string;
  /**
   * Aria-valuetext for screen-readers
   */
  ariaValuetext?: string;
  /**
   * Determines the initial positions of the thumb.
   */
  defaultValue?: number;
  /**
   * If true the thumb can't be moved.
   */
  disabled?: boolean;
  /**
   * Inverts the slider.
   */
  invert?: boolean;
  /**
   * The maximum value of the slider.
   */
  max: number;
  /**
   * The minimum value of the slider.
   */
  min: number;
  /**
   * Callback called only after moving a thumb has ended.
   */
  onAfterChange?: (value: number) => void;
  /**
   * Callback called before starting to move a thumb.
   */
  onBeforeChange?: (value: number) => void;
  /**
   * Callback called on every value change.
   */
  onChange?: (value: number) => void;
  /**
   * Callback called when the the slider is clicked (thumb or tracks).
   */
  onSliderClick?: (value: number) => void;
  /**
   * Determines whether the slider moves horizontally (from left to right) or vertically (from bottom to top).
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * If an additional input should be shown to display and change the current value.
   */
  showValueInput?: boolean;
  /**
   * Value to be added or subtracted on each step the slider makes.
   */
  step?: number;
  /**
   * The title is shown above the Slider as a label.
   */
  title?: string;
  /**
   * Like defaultValue but for controlled components.
   */
  value?: number; // Sets initial value and also sets component to controlled mode
}

/**
 * Slider component
 *
 * @param properties the {@link SliderProps} properties
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function Slider({
  ariaLabel,
  ariaLabelledby,
  ariaValuetext,
  className = '',
  defaultValue = 0,
  disabled,
  invert,
  max,
  min,
  onAfterChange,
  onBeforeChange,
  onChange,
  onSliderClick,
  orientation = 'horizontal',
  showValueInput = false,
  step,
  title,
  value,
  ...props
}: Readonly<SliderProps>): JSX.Element {
  // Used Library has unintuitive orientation for vertical orientation
  const isInvert = orientation === 'vertical' ? !invert : invert;
  const label = title && <div className="ehs-slider-title">{title}</div>;
  const [inputValue, setInputValue] = useState(defaultValue || 0);
  const changeFunctions = useCallback(
    (value: number): void => {
      if (onChange) {
        onChange(value);
      }
      setInputValue(value);
    },
    [onChange]
  );
  const valueInput = useMemo((): JSX.Element => {
    const debounceOnChange = _.debounce(function (value) {
      changeFunctions(value);
    }, 500);
    const error = () => {
      if (inputValue >= min && inputValue <= max) {
        return '';
      }
      if (inputValue < min) {
        return intlFmtTemplateString('react.field.number.min', {
          max: min.toString()
        });
      }
      if (inputValue > max) {
        return intlFmtTemplateString('react.field.number.max', {
          max: max.toString()
        });
      }
    };
    return showValueInput ? (
      <NumberInput
        label={intl('common.value')}
        className="ehs-slider-input"
        onChange={value => debounceOnChange(value)}
        value={inputValue}
        condensed={true}
        error={error()}
        onBlur={() => {
          if (inputValue === undefined) {
            debounceOnChange(defaultValue);
          }
        }}
      />
    ) : (
      <></>
    );
  }, [changeFunctions, defaultValue, inputValue, max, min, showValueInput]);

  useLayoutEffect(() => {
    if (value) {
      setInputValue(value);
    }
    return () => {
      setInputValue(0);
    };
  }, [value]);
  const SliderHeaderComponent = orientation === 'horizontal' ? HBox : VBox;

  return (
    <VBox {...props} className="ehs-slider-container">
      {(title || showValueInput) && (
        <SliderHeaderComponent className={`ehs-slider-header ${orientation}`}>
          {label}
          {valueInput}
        </SliderHeaderComponent>
      )}
      <ReactSlider
        ariaLabel={ariaLabel}
        ariaLabelledby={ariaLabelledby}
        ariaValuetext={ariaValuetext}
        className={`ehs-slider ${orientation} ${className}`.trim()}
        orientation={orientation}
        disabled={disabled}
        defaultValue={defaultValue}
        invert={isInvert}
        max={max}
        min={min}
        onAfterChange={onAfterChange}
        onBeforeChange={onBeforeChange}
        onChange={value => changeFunctions(value)}
        onSliderClick={onSliderClick}
        step={step}
        thumbActiveClassName="ehs-slider-active-thumb"
        thumbClassName="ehs-slider-thumb"
        trackClassName={'ehs-slider-track ' + orientation}
        value={inputValue}
      />
    </VBox>
  );
}
